import {
  PriceComparison,
  PriceComparisonFooter,
} from '@Features/MaterialRequest/requests/components/ComparisonSheet';
import {
  ComparisonSheetSelectedItem,
  ComparisonSheetState,
  PriceComparisonDto,
  TenderSupplier,
} from '@Features/MaterialRequest/requests/components/ComparisonSheet/ComparisonSheet.types';
import { VendorQuantitySplit } from '@Features/MaterialRequest/requests/components/ComparisonSheet/modals/VendorQuantitySplit';
import {
  VendorQuantitySplitFormValues,
  VendorQuantitySplitProps,
} from '@Features/MaterialRequest/requests/components/ComparisonSheet/modals/VendorQuantitySplit/VendorQuantitySplit';
import { Flex } from 'antd';
import { useCallback, useState } from 'react';
import { useFormikContext } from 'formik';
import { useNavigate, useParams } from 'react-router-dom';
import MRFeatureRoutePath from '@Features/router/mr/MRFeatureRoutePath.enum';
import {
  useGetTenderTotalAmountQuery,
  useSaveTenderQuantityMutation,
  useUnlockTenderPricingMutation,
} from '@Features/MaterialRequest/requests/TenderRequest/store/api/TenderRequestApiSlice';
import {
  ComparisonDataList,
  TenderSaveQuantityRequest,
} from '@Types/api/form/tenderRequest/TenderSaveQuantityRquest';
import {
  getQuantitySplit,
  getSplitMaterialIDs,
} from '@Features/MaterialRequest/requests/components/ComparisonSheet/utils';
import { UnlockPricingDialog } from '@Features/MaterialRequest/requests/components/ComparisonSheet/modals/UnlockPricingDialog';
import dayjs from 'dayjs';
import { TenderUnlockPricingRequest } from '@Types/api/form/tenderRequest/TenderUnlockPricingRequest';
import { UnlockPricingFormValues } from '@Features/MaterialRequest/requests/components/ComparisonSheet/modals/UnlockPricingDialog/UnlockPricingDialog';
import { VendorPopup } from '@Features/MaterialRequest/requests/components/ComparisonSheet/modals/VendorPopup';
import PriceBreakdown from '@Features/MaterialRequest/requests/components/ComparisonSheet/modals/PriceBreakdown/desktop/PriceBreakdownDesktop';

interface MaterialsTabDesktopProps {
  comparisonSheetSuppliers: TenderSupplier[];
  comparisonSheetOffers: PriceComparisonDto[];
}

const MaterialsTabDesktop: React.FC<MaterialsTabDesktopProps> = ({
  comparisonSheetSuppliers,
  comparisonSheetOffers,
}) => {
  const { tenderId } = useParams();
  const navigate = useNavigate();
  const formik = useFormikContext<ComparisonSheetState>();
  const [saveTenderQuantity] = useSaveTenderQuantityMutation();
  const [unlockPricing] = useUnlockTenderPricingMutation();
  const { data: totalAmountData } = useGetTenderTotalAmountQuery(tenderId!, {
    skip: !tenderId,
  });

  const [splitInfo, setSplitInfo] = useState<
    VendorQuantitySplitProps['info'] | null
  >(null);
  const [showUnlock, setShowUnlock] = useState<boolean>(false);
  const [vendorDetails, setVendorDetails] = useState<TenderSupplier>();
  const [showPriceBreakdown, setShowPriceBreakdown] = useState<boolean>(false);

  const selectionChange = useCallback(
    (newSelection?: Array<ComparisonSheetSelectedItem>): void => {
      formik.setFieldValue('selectedOffersIDs', newSelection);
      formik.setFieldValue('splitMaterials', getSplitMaterialIDs(newSelection));
      formik.handleSubmit();
    },
    [formik]
  );

  const handleApprove = useCallback(async () => {
    navigate(MRFeatureRoutePath.COMPARISON_SHEET_SELECTED);
  }, [navigate]);

  const handleSaveSplit = ({ offers }: VendorQuantitySplitFormValues): void => {
    const material = splitInfo?.material;
    const requiredQty = splitInfo?.material.requiredQty;
    const tenderItemId = splitInfo?.offers[0].offer.tenderItemId;

    if (material && tenderItemId && requiredQty) {
      const adjustedOffers = getQuantitySplit(requiredQty, offers);
      const dataList: Partial<ComparisonDataList> = {
        tenderItemId: Number(tenderItemId),
        matID: Number(material.matId),
        matCode: String(material.code),
        material: String(material.description),
        reqQty: requiredQty,
      };
      for (let i = 1; i <= 5; i += 1) {
        const vendorFromOffer = splitInfo?.offers.find(
          (off) => off.vendor.id === adjustedOffers[i - 1]?.vendorId
        );

        if (vendorFromOffer) {
          const updatedQuantity = adjustedOffers[i - 1].adjustedQty;

          dataList[`vendor${i}Id`] = vendorFromOffer.offer.vendorId;
          // updated quantity
          dataList[`vendor${i}Qty`] = updatedQuantity;
          dataList[`vendor${i}Rate`] = vendorFromOffer.offer.rate;
        } else {
          dataList[`vendor${i}Id`] = 0;
          dataList[`vendor${i}Qty`] = 0;
          dataList[`vendor${i}Rate`] = 0;
        }
      }
      const payload: TenderSaveQuantityRequest = {
        tenderId: Number(tenderId),
        comparisonDataList: [dataList as ComparisonDataList],
      };

      saveTenderQuantity(payload).then(() => {
        setSplitInfo(null);
      });
    }
  };
  const handleUnlockPricing = useCallback(
    (values: UnlockPricingFormValues): void => {
      if (tenderId) {
        const time = dayjs(values.expiryTime);
        const submitDate = dayjs(values.expiryDate)
          .hour(time.hour())
          .minute(time.minute())
          .second(time.second())
          .toISOString();
        const body: TenderUnlockPricingRequest = {
          discount: values.expectedAmount || 0,
          submitDate,
          tenderId: Number(tenderId),
          remarks: values.remarks || '',
        };
        unlockPricing(body).then(() => {
          setShowUnlock(false);
        });
      }
    },
    [tenderId, unlockPricing]
  );

  const handleShowVendorDetails = (vendor: TenderSupplier): void => {
    setVendorDetails(vendor);
  };

  return (
    <>
      <Flex flex="1 1 100%" className="overflow-hidden" align="stretch">
        <PriceComparison
          vendors={comparisonSheetSuppliers}
          dataSource={comparisonSheetOffers}
          selectionChange={selectionChange}
          formik={formik}
          onSplit={(record) => {
            setSplitInfo(record);
          }}
          handleShowVendorDetails={handleShowVendorDetails}
          totalItems={comparisonSheetOffers.length}
        />
      </Flex>

      <Flex flex="0 1 104px">
        <div style={{ minWidth: '100%' }}>
          <PriceComparisonFooter
            handleReviewApprove={handleApprove}
            handleSave={formik.handleSubmit}
            onUnlockPricing={() => {
              setShowUnlock(true);
            }}
            finalAmount={Number(totalAmountData?.finalTotAmount || 0)}
            handlePriceBreakdown={() => {
              setShowPriceBreakdown(true);
            }}
          />
        </div>
      </Flex>
      {splitInfo && (
        <VendorQuantitySplit
          info={splitInfo}
          onSubmit={(res) => {
            handleSaveSplit(res);
          }}
          onCancel={() => {
            setSplitInfo(null);
          }}
        />
      )}
      {showUnlock && (
        <UnlockPricingDialog
          isOpen
          initialPricing={{
            expectedAmount: 1300,
            expiryDate: '',
            expiryTime: dayjs(),
            remarks: '',
          }}
          onCancel={() => {
            setShowUnlock(false);
          }}
          onSubmit={handleUnlockPricing}
        />
      )}

      {vendorDetails && (
        <VendorPopup
          vendorId={vendorDetails?.id}
          isOpened={Boolean(vendorDetails?.id)}
          title={vendorDetails?.name}
          onClose={() => {
            setVendorDetails(undefined);
          }}
        />
      )}

      {showPriceBreakdown && totalAmountData && (
        <PriceBreakdown
          total={totalAmountData}
          onClose={() => {
            setShowPriceBreakdown(false);
          }}
        />
      )}
    </>
  );
};
export default MaterialsTabDesktop;
